import React, { Fragment, useState, useRef, useCallback } from 'react';
import { add as addToCartService } from '../Services/Cart.service';
import { receive, loadError, update } from '../Actions/Cart.action';
import { catchError } from '../Actions/Error.action';
import { useDispatch, useSelector } from 'react-redux';
import { translate } from '../Services/translation';
import ContactUsModal from './ContactUsModal';
import { debounce } from 'lodash';
import CloseIcon from '@mui/icons-material/Close';

const CustomBuyButton = ({ articleNumber, isBuyable, context, quantityIncrement }) => {
    const minQuantity = quantityIncrement;
    const incQuantity = quantityIncrement;

    const dispatch = useDispatch();
    const inputField = useRef(null);
    const componentRef = useRef(null);
    const [overlayVisible, setOverlayVisible] = useState(false);
    const [overlayText, setOverlayText] = useState({});
    const [showOverlayCloseButton, setShowOverlayCloseButton] = useState(false);
    const [isModalOpen, setModalOpen] = useState(false); // "Contact us" modal
    const isBusinessCustomer = window?.__litium?.preloadState?.myPage?.isBusinessCustomer;
    const hideOverlayTimeout = useRef(null);
    const debounceTime = 1500; // Time delay in ms until textinput is processed after last keypress
    const overlayTime = 2500; // Time in ms for overlay to be shown

    const cart = useSelector((state) => state.cart);
    const currentItem = cart.orderRows.find((x) => x.articleNumber == articleNumber);
    const hasQuantity = currentItem?.quantity > 0;
    const [quantity, setQuantity] = useState(currentItem?.quantity || minQuantity);

    const modalOpen = () => setModalOpen(true);
    const modalClose = () => setModalOpen(false);

    const marginFix = 20; // Adjust for margin if needed

    const generateOverlayText = (quantity, correctionNeeded) => {
        let mainText,
            additionalText = '';

        if (quantity === 0) {
            mainText = translate('code.products.addtocart.quantity.removed');
        } else {
            if (correctionNeeded) {
                mainText = translate('code.products.addtocart.quantity.notvalid');
                additionalText = `This item can only be purchased in increments of ${incQuantity}, at the assigned sales location. Please try again with a number of items allowed.`;
            } else mainText = `${translate('code.products.addtocart.quantity.added')} ${quantity}`;
        }

        return { mainText, additionalText };
    };

    const onAddToCartClick = async () => {
        try {
            const cart = await addToCartService({ articleNumber: articleNumber, quantity: minQuantity });
            dispatch(receive(cart));

            setOverlayText(generateOverlayText(minQuantity, false));
            setQuantity(minQuantity);
            setOverlayVisible(true);

            resetOverlayTimer();

            return true;
        } catch (ex) {
            dispatch(catchError(ex, (error) => loadError(error)));
            return false;
        }
    };

    const onClickMinus = async () => {
        onClickQuantityStep(-1);
    };

    const onClickPlus = async () => {
        onClickQuantityStep(1);
    };

    const onClickQuantityStep = async (numberSign) => {
        let value = parseInt(inputField.current.value);

        let remainder = 0;
        if (incQuantity > 1 && quantity > 0) remainder = quantity % incQuantity;

        let step = 0;
        if (numberSign > 0)
            step = incQuantity - remainder; // Step up to closest allowed value
        else if (remainder != 0)
            step = -remainder; // Go to closest allowed below
        else step = -incQuantity; // Step minus

        let newValue = value + step; // Add or subtract increment (step) value

        if (newValue < minQuantity) newValue = 0;

        setQuantity(newValue);
        onInputChange(newValue);
    };

    const onInputChange = useCallback(
        debounce(async (quantity) => {
            try {
                let correctionNeeded = false;

                if (incQuantity > 1 && quantity > 0) {
                    let remainder = quantity % incQuantity;
                    if (remainder !== 0) correctionNeeded = true;
                }

                inputField.current.value = quantity;

                if (!correctionNeeded) dispatch(update(articleNumber, quantity));

                setOverlayText(generateOverlayText(quantity, correctionNeeded));
                setQuantity(quantity);
                setShowOverlayCloseButton(correctionNeeded);
                setOverlayVisible(true);

                if (!correctionNeeded) {
                    resetOverlayTimer();
                }

                return true;
            } catch (ex) {
                dispatch(catchError(ex, (error) => loadError(error)));
                return false;
            }
        }, debounceTime),
        [incQuantity]
    );

    const resetOverlayTimer = () => {
        if (hideOverlayTimeout.current) {
            clearTimeout(hideOverlayTimeout.current);
        }
        hideOverlayTimeout.current = setTimeout(() => {
            setOverlayVisible(false);
        }, overlayTime);
    };

    const closeOverlay = () => {
        setOverlayVisible(false);
    };

    const calculateOverlaySize = () => {
        if (!componentRef.current) return { width: 0, height: 0 };

        let rowElement;
        // Determine the context to find the appropriate parent element.
        if (context === 'productList') {
            rowElement = componentRef.current.closest('tr');
        } else if (context === 'shoppingCart' || context === 'checkout') {
            rowElement = componentRef.current.closest('.overlay-finder');
        } else if (context === 'pdp') {
            rowElement = componentRef.current.closest('.product-detail__container').querySelector('.overlay-finder');
        }

        if (!rowElement) return { width: 0, height: 0 };

        //future better implementation.
        //  console.log('total', rowElement.childNodes[0].getBoundingClientRect().left)

        const totalWidth = rowElement.offsetWidth;
        const totalHeight = rowElement.offsetHeight;
        let lastCell;

        // If the context is a product list, calculate the size excluding the last cell.
        if (context === 'productList') {
            lastCell = rowElement.lastElementChild.offsetWidth;

            const lastCellWidth = lastCell;

            return {
                width: totalWidth - lastCellWidth + marginFix + 'px',
                height: totalHeight + 'px',
            };

            // For shopping cart or checkout, find the image and info cells.
        } else if (['shoppingCart', 'checkout'].includes(context)) {
            let imageCell = rowElement.querySelector('.checkout-cart__image-wrapper');
            let infoCell = rowElement.querySelector('.checkout-cart__image-info');

            //CSS gap on 30px.....TODO find out how to make it dymnamic or replace 30
            let totalWidth = imageCell.offsetWidth + infoCell.offsetWidth + 30;

            // Adjust for mobile screen sizes.
            if (screen.width < 1024) {
                let totalHeight = imageCell.offsetHeight + infoCell.offsetHeight;
                return {
                    width: '100%',
                    height: totalHeight + 'px',
                };
            }

            return {
                width: totalWidth + marginFix + 'px',
                height: totalHeight + 'px',
            };
        } else if (context === 'pdp') {
            let imageCell = rowElement.querySelector('.product-detail__images');
            let totalHeight = imageCell.offsetHeight;

            return {
                width: totalWidth + 'px',
                height: totalHeight + 'px',
            };
        }
    };

    if (!isBusinessCustomer) {
        return null;
    }

    let overlaySize = calculateOverlaySize();

    return (
        <Fragment>
            {isBuyable ? (
                <div
                    ref={componentRef}
                    className="buy-button__container"
                >
                    {!hasQuantity && (
                        <button
                            className="button buy-button"
                            onClick={() => onAddToCartClick()}
                        >
                            {translate('code.products.addtocart.button')}
                        </button>
                    )}
                    <div
                        className="input-container"
                        style={{ display: hasQuantity ? 'flex' : 'none' }}
                    >
                        <button
                            className="input-controller input-controller--minus"
                            onClick={() => onClickMinus()}
                            style={{ display: hasQuantity ? 'flex' : 'none' }}
                        >
                            -
                        </button>
                        <input
                            className="buy-button__quantity"
                            type="number"
                            min={minQuantity}
                            step={incQuantity}
                            value={quantity}
                            id={articleNumber}
                            onChange={(event) => {
                                let value = event.target.valueAsNumber;
                                onInputChange(value);
                                setQuantity(value);
                            }}
                            ref={inputField}
                            style={{ display: hasQuantity ? 'block' : 'none' }}
                        />
                        <button
                            className="input-controller input-controller--plus"
                            onClick={(event) => onClickPlus()}
                            style={{ display: hasQuantity ? 'flex' : 'none' }}
                        >
                            +
                        </button>
                    </div>
                    <div
                        className="overlay"
                        style={{
                            width: `${overlaySize.width}`,
                            height: `${overlaySize.height}`,
                            opacity: overlayVisible ? 1 : 0,
                            marginLeft: -marginFix,
                            pointerEvents: overlayVisible ? 'all' : 'none',
                        }}
                    >
                        <span
                            className={`overlay__close  ${showOverlayCloseButton ? 'overlay__show-close-button' : ''} `}
                            onClick={() => closeOverlay()}
                        >
                            <CloseIcon style={{ fontSize: '2rem' }} />
                        </span>
                        {overlayText.mainText && <p>{overlayText.mainText}</p>}
                        {overlayText.additionalText && (
                            <p className="overlay__additional-text">{overlayText.additionalText}</p>
                        )}
                    </div>
                </div>
            ) : (
                <div className="buy-button__container">
                    <button
                        className="button button-secondary buy-button contact-us-button"
                        onClick={() => modalOpen()}
                    >
                        {translate('code.products.contactus.button')}
                    </button>
                    <ContactUsModal
                        isOpen={isModalOpen}
                        closeModal={modalClose}
                    />
                </div>
            )}
        </Fragment>
    );
};

export default CustomBuyButton;
